import { useQuery } from "@tanstack/react-query";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
    ArrowLeftIcon,
    UserCircleIcon,
    MapPinIcon,
    TruckIcon,
    CalendarIcon,
    ExclamationTriangleIcon,
    PhoneIcon,
    EnvelopeIcon,
    CurrencyDollarIcon,
    CheckCircleIcon,
    XCircleIcon,
    ArrowPathIcon,
    BoltIcon,
} from "@heroicons/react/24/outline";

import { Button } from "@/components/ui/Button";
import { trackDriver } from "../api/liveMonitoringApi";

// ============================================================================
// Helpers
// ============================================================================

const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return "—";
    try {
        return new Intl.DateTimeFormat("en-NG", {
            month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit",
        }).format(new Date(dateStr));
    } catch {
        return dateStr;
    }
};

const formatCurrency = (amount: number | null | undefined, currency = "NGN") => {
    if (amount == null) return "—";
    return new Intl.NumberFormat("en-NG", { style: "currency", currency, maximumFractionDigits: 2 }).format(amount);
};

const InfoRow = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <div className="flex items-start justify-between py-2.5">
        <span className="text-sm text-slate-500 dark:text-slate-400">{label}</span>
        <span className="text-right text-sm font-medium text-slate-900 dark:text-white">{children}</span>
    </div>
);

// ============================================================================
// Component
// ============================================================================

export const LiveDriverDetailsRoute = () => {
    const { driverId } = useParams<{ driverId: string }>();
    const navigate = useNavigate();

    const { data, isLoading, error, refetch, isFetching } = useQuery({
        queryKey: ["live-driver-track", driverId],
        queryFn: () => trackDriver(driverId!),
        enabled: !!driverId,
        refetchInterval: 10_000,
    });

    const d = data?.data;

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-20">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
            </div>
        );
    }

    if (error || !d) {
        return (
            <div className="rounded-2xl border border-rose-200 bg-rose-50 p-8 text-center dark:border-rose-800 dark:bg-rose-900/20">
                <ExclamationTriangleIcon className="mx-auto h-10 w-10 text-rose-400" />
                <h3 className="mt-3 text-lg font-semibold text-rose-700 dark:text-rose-300">Driver not found</h3>
                <Button variant="secondary" size="sm" className="mt-4" onClick={() => navigate("/live-monitoring/drivers")}>
                    Back to Drivers
                </Button>
            </div>
        );
    }

    const driver = d.driver;
    const loc = d.currentLocation;
    const vehicle = d.vehicle;
    const stats = d.todayStats;

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="flex items-start gap-4">
                    <button onClick={() => navigate("/live-monitoring/drivers")} className="mt-1 rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800 dark:hover:text-slate-300">
                        <ArrowLeftIcon className="h-5 w-5" />
                    </button>
                    <div>
                        <div className="flex items-center gap-3">
                            <UserCircleIcon className="h-7 w-7 text-primary-500" />
                            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
                                {driver.firstName} {driver.lastName}
                            </h1>
                        </div>
                        <div className="mt-2 flex items-center gap-3">
                            <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium ${d.isOnline ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400" : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400"}`}>
                                <span className={`h-2 w-2 rounded-full ${d.isOnline ? "bg-emerald-500 animate-pulse" : "bg-slate-400"}`} />
                                {d.isOnline ? "Online" : "Offline"}
                            </span>
                            {loc && (
                                <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium ${loc.status === "AVAILABLE" ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400" : "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"}`}>
                                    {loc.status}
                                </span>
                            )}
                            <span className="rounded-full bg-slate-100 px-3 py-1.5 text-sm font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-400">
                                {driver.accountStatus}
                            </span>
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <Link to={`/drivers/${driverId}`}>
                        <Button variant="secondary" size="sm">Full Profile</Button>
                    </Link>
                    <Button variant="secondary" size="sm" onClick={() => refetch()} disabled={isFetching}>
                        <ArrowPathIcon className={`mr-1.5 h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
                        Refresh
                    </Button>
                </div>
            </div>

            {/* Today Stats */}
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
                    <div className="flex items-center gap-2">
                        <TruckIcon className="h-5 w-5 text-primary-500" />
                        <div>
                            <p className="text-xs text-slate-500 dark:text-slate-400">Total Trips</p>
                            <p className="text-xl font-bold text-slate-900 dark:text-white">{stats.totalTrips}</p>
                        </div>
                    </div>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
                    <div className="flex items-center gap-2">
                        <CheckCircleIcon className="h-5 w-5 text-emerald-500" />
                        <div>
                            <p className="text-xs text-slate-500 dark:text-slate-400">Completed</p>
                            <p className="text-xl font-bold text-emerald-600">{stats.completed}</p>
                        </div>
                    </div>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
                    <div className="flex items-center gap-2">
                        <XCircleIcon className="h-5 w-5 text-rose-500" />
                        <div>
                            <p className="text-xs text-slate-500 dark:text-slate-400">Cancelled</p>
                            <p className="text-xl font-bold text-rose-600">{stats.cancelled}</p>
                        </div>
                    </div>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
                    <div className="flex items-center gap-2">
                        <CurrencyDollarIcon className="h-5 w-5 text-amber-500" />
                        <div>
                            <p className="text-xs text-slate-500 dark:text-slate-400">Earnings</p>
                            <p className="text-xl font-bold text-amber-600">{formatCurrency(stats.earnings)}</p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="grid gap-6 lg:grid-cols-2">
                {/* Driver Info */}
                <div className="rounded-2xl border border-slate-200 bg-white p-6 dark:border-slate-800 dark:bg-slate-900">
                    <h2 className="mb-4 text-lg font-semibold text-slate-900 dark:text-white">Driver Details</h2>
                    <div className="divide-y divide-slate-100 dark:divide-slate-800">
                        <InfoRow label="Email">
                            <div className="flex items-center gap-1.5">
                                <EnvelopeIcon className="h-4 w-4 text-slate-400" />
                                {driver.email}
                            </div>
                        </InfoRow>
                        <InfoRow label="Phone">
                            <div className="flex items-center gap-1.5">
                                <PhoneIcon className="h-4 w-4 text-slate-400" />
                                {driver.phoneNumber}
                            </div>
                        </InfoRow>
                        <InfoRow label="Registered">{formatDate(driver.registeredAt)}</InfoRow>
                    </div>
                </div>

                {/* Location */}
                <div className="rounded-2xl border border-slate-200 bg-white p-6 dark:border-slate-800 dark:bg-slate-900">
                    <h2 className="mb-4 text-lg font-semibold text-slate-900 dark:text-white">
                        <MapPinIcon className="mr-2 inline h-5 w-5 text-primary-500" />
                        Current Location
                    </h2>
                    {loc ? (
                        <div className="divide-y divide-slate-100 dark:divide-slate-800">
                            <InfoRow label="Coordinates">{loc.latitude.toFixed(6)}, {loc.longitude.toFixed(6)}</InfoRow>
                            <InfoRow label="Speed">{loc.speed.toFixed(1)} km/h</InfoRow>
                            <InfoRow label="Heading">{loc.heading.toFixed(1)}°</InfoRow>
                            <InfoRow label="Accuracy">{loc.accuracy.toFixed(1)}m</InfoRow>
                            <InfoRow label="Last Update">{formatDate(loc.lastUpdatedAt)}</InfoRow>
                        </div>
                    ) : (
                        <p className="text-sm text-slate-500 dark:text-slate-400">No location data available</p>
                    )}
                </div>
            </div>

            {/* Vehicle */}
            {vehicle && (
                <div className="rounded-2xl border border-slate-200 bg-white p-6 dark:border-slate-800 dark:bg-slate-900">
                    <h2 className="mb-4 text-lg font-semibold text-slate-900 dark:text-white">
                        <TruckIcon className="mr-2 inline h-5 w-5 text-slate-400" />
                        Vehicle
                    </h2>
                    <div className="grid gap-4 sm:grid-cols-2">
                        <div className="divide-y divide-slate-100 dark:divide-slate-800">
                            <InfoRow label="Plate Number">{vehicle.plateNumber}</InfoRow>
                            <InfoRow label="Make">{vehicle.make}</InfoRow>
                            <InfoRow label="Model">{vehicle.model}</InfoRow>
                        </div>
                        <div className="divide-y divide-slate-100 dark:divide-slate-800">
                            <InfoRow label="Color">{vehicle.color}</InfoRow>
                            <InfoRow label="Year">{vehicle.year}</InfoRow>
                            <InfoRow label="Seats">{vehicle.seatCapacity}</InfoRow>
                        </div>
                    </div>
                </div>
            )}

            {/* Current Trip */}
            {d.currentTrip && (
                <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6 dark:border-amber-800 dark:bg-amber-900/20">
                    <h2 className="mb-4 text-lg font-semibold text-amber-800 dark:text-amber-300">
                        <BoltIcon className="mr-2 inline h-5 w-5" />
                        Active Trip
                    </h2>
                    <div className="grid gap-4 sm:grid-cols-2">
                        <div className="divide-y divide-amber-200/50 dark:divide-amber-800/50">
                            <InfoRow label="Trip ID">
                                <Link to={`/live-monitoring/trips/${d.currentTrip.tripId}`} className="text-primary-600 hover:underline dark:text-primary-400">
                                    {d.currentTrip.tripId.slice(0, 12)}…
                                </Link>
                            </InfoRow>
                            <InfoRow label="Status">{d.currentTrip.status}</InfoRow>
                            <InfoRow label="Pickup">{d.currentTrip.pickup?.address || "—"}</InfoRow>
                        </div>
                        <div className="divide-y divide-amber-200/50 dark:divide-amber-800/50">
                            <InfoRow label="Dropoff">{d.currentTrip.dropoff?.address || "—"}</InfoRow>
                            <InfoRow label="Passenger">{d.currentTrip.passenger?.firstName} {d.currentTrip.passenger?.lastName}</InfoRow>
                            <InfoRow label="Fare">{formatCurrency(d.currentTrip.estimatedFare, d.currentTrip.currency)}</InfoRow>
                        </div>
                    </div>
                </div>
            )}

            {/* Recent Trips */}
            {d.recentTrips && d.recentTrips.length > 0 && (
                <div className="rounded-2xl border border-slate-200 bg-white p-6 dark:border-slate-800 dark:bg-slate-900">
                    <h2 className="mb-4 text-lg font-semibold text-slate-900 dark:text-white">Recent Trips</h2>
                    <div className="space-y-2">
                        {d.recentTrips.map((trip) => (
                            <Link
                                key={trip.tripId}
                                to={`/ride-bookings-trips/trips/${trip.tripId}`}
                                className="flex items-center justify-between rounded-xl border border-slate-100 p-3 transition-colors hover:bg-slate-50 dark:border-slate-800 dark:hover:bg-slate-800/50"
                            >
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2">
                                        <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${trip.status === "COMPLETED" ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400" : "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300"}`}>
                                            {trip.status}
                                        </span>
                                        <span className="text-sm font-medium text-slate-900 dark:text-white">{formatCurrency(trip.fare)}</span>
                                    </div>
                                    <p className="mt-1 truncate text-xs text-slate-500 dark:text-slate-400">
                                        {trip.pickupAddress} → {trip.dropoffAddress}
                                    </p>
                                </div>
                                <span className="ml-3 whitespace-nowrap text-xs text-slate-400">{formatDate(trip.createdAt)}</span>
                            </Link>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};
